import React, { useEffect, useState } from "react";
import {
  Card, Button, Table, Switch, Modal, Input, Select, Tag,
  Popconfirm, notification, Tooltip, Typography, Space,
} from "antd";
import {
  PlusOutlined, DeleteOutlined, EditOutlined, PlayCircleOutlined,
  ClockCircleOutlined, ReloadOutlined, SendOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { baseUrl } from "../../../config";

const { Text } = Typography;
const { Option } = Select;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
});

const FREQUENCY_OPTIONS = [
  { value: "daily",    label: "Daily" },
  { value: "weekdays", label: "Weekdays (Mon–Fri)" },
  { value: "custom",   label: "Custom Days" },
];

const DAY_OPTIONS = [
  { value: "0", label: "Sun" },
  { value: "1", label: "Mon" },
  { value: "2", label: "Tue" },
  { value: "3", label: "Wed" },
  { value: "4", label: "Thu" },
  { value: "5", label: "Fri" },
  { value: "6", label: "Sat" },
];

// Fields available as WhatsApp template params (matches PosterDesigner tokens)
const PARAM_FIELDS = [
  { value: "name",          label: "Name (नाम)" },
  { value: "relation",      label: "Relation (रिश्ता)" },
  { value: "father_name",   label: "Father Name (पिता का नाम)" },
  { value: "village",       label: "Village (ग्राम)" },
  { value: "district",      label: "District (जिला)" },
  { value: "address",       label: "Full Address (पूरा पता)" },
  { value: "phone",         label: "Phone (फोन)" },
  { value: "birth_date",    label: "Birth Date (जन्म तिथि)" },
  { value: "birth_day",     label: "Birth Day (दिन)" },
  { value: "birth_month",   label: "Birth Month (माह)" },
  { value: "birth_year",    label: "Birth Year (वर्ष)" },
];

const emptyForm = { schedule_name: "", trigger_key: "", send_time: "06:00", frequency: "daily", custom_days: "", campaign_name: "", template_params: ["name"] };

export default function ScheduleManager() {
  const [schedules, setSchedules]       = useState([]);
  const [loading, setLoading]           = useState(false);
  const [runModal, setRunModal]         = useState(null);  // schedule row for date-picker modal
  const [runDate, setRunDate]           = useState('');    // YYYY-MM-DD
  const [running, setRunning]           = useState(false);
  const [addModal, setAddModal]         = useState(false);
  const [editModal, setEditModal]       = useState(null); // schedule row being edited
  const [form, setForm]                 = useState(emptyForm);
  const [saving, setSaving]             = useState(false);

  // ── Test Send state ────────────────────────────────────────────────────────
  const [testModal, setTestModal]       = useState(null); // schedule row being tested
  const [testPhone, setTestPhone]       = useState('');
  const [testing, setTesting]           = useState(false);

  useEffect(() => { fetchSchedules(); }, []);

  const fetchSchedules = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/poster-config/schedules`, { headers: authHeaders() });
      if (res.data.status) setSchedules(res.data.result || []);
    } catch (e) {
      notification.error({ message: "Failed to load schedules.", placement: "topRight" });
    } finally {
      setLoading(false);
    }
  };

  // ── Toggle active ─────────────────────────────────────────────────────────
  const handleToggle = async (id, checked) => {
    try {
      const res = await axios.patch(
        `${baseUrl}/poster-config/schedules/${id}`,
        { is_active: checked ? 1 : 0 },
        { headers: authHeaders() }
      );
      if (res.data.status) {
        notification.success({
          message: checked ? "Schedule enabled." : "Schedule paused.",
          placement: "topRight",
        });
        fetchSchedules();
      } else {
        notification.error({ message: res.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" });
    }
  };

  // ── Run Now — open date picker modal ─────────────────────────────────────
  const openRunModal = (row) => {
    setRunDate(new Date().toISOString().slice(0, 10)); // default to today
    setRunModal(row);
  };

  const confirmRunNow = async () => {
    setRunning(true);
    try {
      const res = await axios.post(
        `${baseUrl}/poster-config/schedules/${runModal.id}/run-now`,
        { target_date: runDate || null },
        { headers: authHeaders() }
      );
      if (res.data.status) {
        notification.success({
          message: `"${runModal.schedule_name}" executed for ${runDate || 'today'}.`,
          placement: "topRight",
        });
        setRunModal(null);
        fetchSchedules();
      } else {
        notification.error({ message: res.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" });
    } finally {
      setRunning(false);
    }
  };

  // ── Test Send ─────────────────────────────────────────────────────────────
  const handleTestSend = async () => {
    if (!testPhone.trim()) {
      notification.warning({ message: "Please enter a phone number.", placement: "topRight" });
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(
        `${baseUrl}/poster-config/schedules/${testModal.id}/test-send`,
        { phone_no: testPhone.trim() },
        { headers: authHeaders() }
      );
      if (res.data.status) {
        notification.success({
          message: `Test sent to ${testPhone}`,
          description: `Schedule: "${testModal.schedule_name}"`,
          placement: "topRight",
        });
        setTestModal(null);
        setTestPhone('');
        fetchSchedules();
      } else {
        notification.error({ message: res.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" });
    } finally {
      setTesting(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      const res = await axios.delete(
        `${baseUrl}/poster-config/schedules/${id}`,
        { headers: authHeaders() }
      );
      if (res.data.status) {
        notification.success({ message: "Schedule deleted.", placement: "topRight" });
        fetchSchedules();
      } else {
        notification.error({ message: res.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" });
    }
  };

  // ── Save (create or update) ───────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.schedule_name.trim() || !form.trigger_key.trim() || !form.send_time.trim()) {
      notification.warning({ message: "Name, Trigger Key and Time are required.", placement: "topRight" });
      return;
    }
    if (!/^\d{1,2}:\d{2}$/.test(form.send_time)) {
      notification.warning({ message: "Time must be in HH:MM format (e.g. 06:00).", placement: "topRight" });
      return;
    }
    setSaving(true);
    try {
      let res;
      if (editModal) {
        res = await axios.patch(
          `${baseUrl}/poster-config/schedules/${editModal.id}`,
          form,
          { headers: authHeaders() }
        );
      } else {
        res = await axios.post(
          `${baseUrl}/poster-config/schedules/create`,
          form,
          { headers: authHeaders() }
        );
      }
      if (res.data.status) {
        notification.success({ message: res.data.message, placement: "topRight" });
        setAddModal(false);
        setEditModal(null);
        setForm(emptyForm);
        fetchSchedules();
      } else {
        notification.error({ message: res.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" });
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (row) => {
    let tp = ["name"];
    try { tp = JSON.parse(row.template_params || '["name"]'); } catch (_) {}
    setForm({
      schedule_name:   row.schedule_name,
      trigger_key:     row.trigger_key,
      send_time:       row.send_time,
      frequency:       row.frequency,
      custom_days:     row.custom_days || "",
      campaign_name:   row.campaign_name || "",
      template_params: tp,
    });
    setEditModal(row);
  };

  // ── Table columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Schedule Name",
      dataIndex: "schedule_name",
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: "Trigger Key",
      dataIndex: "trigger_key",
      render: (v) => <Tag color="geekblue" style={{ fontFamily: "monospace" }}>{v}</Tag>,
    },
    {
      title: "Time",
      dataIndex: "send_time",
      render: (v) => (
        <span>
          <ClockCircleOutlined style={{ marginRight: 4, color: "#1890ff" }} />
          {v} IST
        </span>
      ),
    },
    {
      title: "Frequency",
      dataIndex: "frequency",
      render: (v, row) => {
        const label = FREQUENCY_OPTIONS.find(f => f.value === v)?.label || v;
        if (v === "custom" && row.custom_days) {
          const dayLabels = row.custom_days.split(",")
            .map(d => DAY_OPTIONS.find(o => o.value === d.trim())?.label || d).join(", ");
          return <span>{label} <Text type="secondary" style={{ fontSize: 11 }}>({dayLabels})</Text></span>;
        }
        return <span>{label}</span>;
      },
    },
    {
      title: "Campaign Name",
      dataIndex: "campaign_name",
      render: (v) => v
        ? <Tag color="purple" style={{ fontFamily: "monospace", fontSize: 11 }}>{v}</Tag>
        : <Text type="secondary" style={{ fontSize: 11 }}>default</Text>,
    },
    {
      title: "Template Params",
      dataIndex: "template_params",
      render: (v) => {
        let fields = ["name"];
        try { fields = JSON.parse(v || '["name"]'); } catch (_) {}
        return (
          <span>
            {fields.map(f => <Tag key={f} color="cyan" style={{ fontSize: 10 }}>{f}</Tag>)}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "is_active",
      render: (v, row) => (
        <Switch
          checked={!!v}
          onChange={(checked) => handleToggle(row.id, checked)}
          checkedChildren="On"
          unCheckedChildren="Off"
        />
      ),
    },
    {
      title: "Last Run",
      dataIndex: "last_run",
      render: (v, row) => {
        if (!v) return <Text type="secondary" style={{ fontSize: 11 }}>Never</Text>;
        const d = new Date(v);
        const dateStr = d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false });
        const status  = row.last_status;
        const isError = status && status.startsWith("Error");
        return (
          <Tooltip title={status || ""}>
            <div style={{ fontSize: 11 }}>
              <div>{dateStr}</div>
              {status && (
                <Tag color={isError ? "red" : "green"} style={{ marginTop: 2, fontSize: 10 }}>
                  {isError ? "Error" : status.startsWith("Manual") ? "Manual" : "OK"}
                </Tag>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      render: (_, row) => (
        <Space>
          <Button
            size="small"
            type="default"
            icon={<EditOutlined />}
            onClick={() => openEdit(row)}
          >
            Edit
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={() => openRunModal(row)}
          >
            Run Now
          </Button>
          <Button
            size="small"
            icon={<SendOutlined />}
            style={{ borderColor: '#52c41a', color: '#52c41a' }}
            onClick={() => { setTestModal(row); setTestPhone(''); }}
          >
            Test
          </Button>
          <Popconfirm
            title="Delete this schedule?"
            onConfirm={() => handleDelete(row.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const isEditing = !!editModal;

  return (
    <>
      <Card
        bordered={false}
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
              <ReloadOutlined style={{ marginRight: 6 }} />
              Schedule Manager
            </span>
            <Button size="small" icon={<PlusOutlined />} onClick={() => { setForm(emptyForm); setAddModal(true); }}>
              Add Schedule
            </Button>
          </div>
        }
        headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
        style={{ backgroundColor: "#fff", borderRadius: 8, marginBottom: 16 }}
        bodyStyle={{ padding: "16px" }}
      >
        <p style={{ color: "#666", fontSize: 12, marginBottom: 12 }}>
          Each schedule runs a registered handler at the configured time.
          Toggle <b>On/Off</b> to pause or resume without deleting.
          Use <b>Run Now</b> to test immediately.
          Changes take effect instantly — no server restart required.
        </p>
        <Table
          dataSource={schedules}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={false}
          locale={{ emptyText: "No schedules found. Click \"Add Schedule\" to get started." }}
        />
      </Card>

      {/* Run Now — Date Picker Modal */}
      <Modal
        title={<span><PlayCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />Run Now — {runModal?.schedule_name}</span>}
        open={!!runModal}
        onOk={confirmRunNow}
        onCancel={() => setRunModal(null)}
        confirmLoading={running}
        okText="Run"
        cancelText="Cancel"
      >
        <p style={{ color: '#666', fontSize: 13, marginBottom: 12 }}>
          Select the date to run this schedule for. Use a past date to resend messages that were missed (e.g. due to API key issues).
        </p>
        <Text strong>Date</Text>
        <br />
        <input
          type="date"
          value={runDate}
          max={new Date().toISOString().slice(0, 10)}
          onChange={e => setRunDate(e.target.value)}
          style={{
            marginTop: 6,
            width: '100%',
            padding: '6px 11px',
            border: '1px solid #d9d9d9',
            borderRadius: 6,
            fontSize: 14,
            lineHeight: '1.5715',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <Text type="secondary" style={{ fontSize: 11, marginTop: 6, display: 'block' }}>
          Defaults to today. Only past dates or today are allowed.
        </Text>
      </Modal>

      {/* Test Send Modal */}
      <Modal
        title={<span><SendOutlined style={{ marginRight: 8, color: '#52c41a' }} />Test Send — {testModal?.schedule_name}</span>}
        open={!!testModal}
        onOk={handleTestSend}
        onCancel={() => { setTestModal(null); setTestPhone(''); }}
        confirmLoading={testing}
        okText="Send Now"
        cancelText="Cancel"
        okButtonProps={{ style: { backgroundColor: '#52c41a', borderColor: '#52c41a' } }}
      >
        <p style={{ color: '#666', fontSize: 13, marginBottom: 12 }}>
          Enter a phone number to send a test poster to that specific person.
          The poster will be generated and sent immediately via WhatsApp — skipping any birthday/payment conditions.
        </p>
        <Text strong>Phone Number</Text>
        <Input
          value={testPhone}
          onChange={e => setTestPhone(e.target.value.replace(/\D/g, ''))}
          placeholder="e.g. 9876543210"
          prefix="+91"
          maxLength={10}
          style={{ marginTop: 6 }}
          onPressEnter={handleTestSend}
        />
        <Text type="secondary" style={{ fontSize: 11, marginTop: 6, display: 'block' }}>
          Enter 10-digit mobile number without country code.
        </Text>
      </Modal>

      {/* Add / Edit Schedule Modal */}
      <Modal
        title={isEditing ? "Edit Schedule" : "Add New Schedule"}
        open={addModal || isEditing}
        onOk={handleSave}
        onCancel={() => { setAddModal(false); setEditModal(null); setForm(emptyForm); }}
        confirmLoading={saving}
        okText={isEditing ? "Update" : "Create"}
        cancelText="Cancel"
      >
        <div style={{ marginBottom: 12 }}>
          <Text strong>Schedule Name</Text>
          <Input
            value={form.schedule_name}
            onChange={e => setForm(f => ({ ...f, schedule_name: e.target.value }))}
            placeholder="e.g. Birthday Poster (Daily)"
            style={{ marginTop: 4 }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <Text strong>Trigger Key</Text>
          <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
            Must match a registered handler (e.g. birthday, payment_reminder)
          </Text>
          <Input
            value={form.trigger_key}
            onChange={e => setForm(f => ({ ...f, trigger_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") }))}
            placeholder="e.g. birthday"
            style={{ marginTop: 4 }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <Text strong>Send Time (IST)</Text>
          <Input
            value={form.send_time}
            onChange={e => setForm(f => ({ ...f, send_time: e.target.value }))}
            placeholder="HH:MM — e.g. 06:00"
            style={{ marginTop: 4, width: 140 }}
            maxLength={5}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <Text strong>Frequency</Text>
          <Select
            value={form.frequency}
            onChange={v => setForm(f => ({ ...f, frequency: v, custom_days: "" }))}
            style={{ width: "100%", marginTop: 4 }}
          >
            {FREQUENCY_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
          </Select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Text strong>WhatsApp Campaign Name</Text>
          <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
            The AiSensy campaign/template name used when sending (e.g. Birthday Message New)
          </Text>
          <Input
            value={form.campaign_name}
            onChange={e => setForm(f => ({ ...f, campaign_name: e.target.value }))}
            placeholder="e.g. Birthday Message New"
            style={{ marginTop: 4 }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <Text strong>Template Params</Text>
          <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
            Fields sent as <code>templateParams</code> to WhatsApp — in order, matching your template placeholders (&#123;&#123;1&#125;&#125;, &#123;&#123;2&#125;&#125;…)
          </Text>
          <Select
            mode="multiple"
            value={form.template_params || ["name"]}
            onChange={vals => setForm(f => ({ ...f, template_params: vals }))}
            style={{ width: "100%", marginTop: 4 }}
            placeholder="Select fields in order..."
          >
            {PARAM_FIELDS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
          </Select>
          <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: "block" }}>
            Example: if template has 2 placeholders → select Name then Village in that order.
          </Text>
        </div>

        {form.frequency === "custom" && (
          <div style={{ marginBottom: 12 }}>
            <Text strong>Days of Week</Text>
            <Select
              mode="multiple"
              value={(form.custom_days || "").split(",").filter(Boolean)}
              onChange={vals => setForm(f => ({ ...f, custom_days: vals.join(",") }))}
              style={{ width: "100%", marginTop: 4 }}
              placeholder="Select days..."
            >
              {DAY_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
            </Select>
          </div>
        )}
      </Modal>
    </>
  );
}
